import React, { useEffect, useState } from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { useLoginContext } from '../../Contexts/LoginContext';
import ButtonPrimary from './ButtonPrimary';
import Button from './Button';
import { IMaskInput } from 'react-imask';
import { TbCircleFilled } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'
import { ImSpinner8 } from 'react-icons/im';
import { HiXMark } from 'react-icons/hi2';
import { useCartContext } from '../../Contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { useStoreContext } from '../../Contexts/StoreContext';
import { useStoreHook } from '../../Hooks/StoreHook';

const validationSchema = Yup.object().shape({
    first_name: Yup.string()
        .min(2, 'Pelo menos 2 caracteres')
        .required('Obrigatório'),
    mobile_phone: Yup.string()
        .min(13, 'Pelo menos 13 caracteres')
        .required('Obrigatório'),
});

interface FormRegisterCustomerCardProps {
    onCancel: CallableFunction;
}

const FormRegisterCustomerCard = ({onCancel}: FormRegisterCustomerCardProps) => {

    const { orderType } = useOrderTypeContext();
    const { setUser, token, setToken, guestToken, setGuestToken, setShowLoginBasic } = useLoginContext();
    const { setShowCart, currentCart } = useCartContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton } = useLayoutContext();
    const [loadingLogin, setLoadingLogin] = useState(false);
    const { currentStore } = useStoreContext();
    const { getLinkToHome } = useStoreHook();
    const navigate = useNavigate();

    const initialData = {
        first_name: '',
        mobile_phone: '',
    }

    useEffect(() => {
        if (currentStore && token && currentCart && currentCart.items.length > 0) {
            navigate(`${getLinkToHome()}/checkout`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleLoginBasic = async (data: any) => {
        const payload = {
            first_name: data.first_name,
            mobile_phone: data.mobile_phone,
        }
        try {
            setLoadingLogin(true);
            const customerService = new CustomerService(orderType, token, guestToken);
            const response = await customerService.basicRegister(payload);
            if (response) {
                setGuestToken('');
                setToken(response.token);
                setUser(response.profile)
                setShowLoginBasic(false);
                setShowCart(false);
            }
        } catch (error) {
            setLoadingLogin(false);
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle('Ops! Algo não deu certo.');
            setMainLoadingMessage('Verifique os dados informados e tente logar novamente.');
            setMainLoadingCloseButton(true)
        }
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    };

    return (
        <>
            <Formik
                initialValues={initialData}
                validationSchema={validationSchema}
                onSubmit={(data) => {
                    handleLoginBasic(data);
                }}
                >
                {({ setFieldValue, values }) => (
                    <Form className="flex flex-col gap-4 justify-between">

                        <div className='h-full grid gap-3 p-4 pt-2 bg-gray-50'>

                            <div className='grid gap-2'>
                                <div className='flex gap-2'>
                                    <div className='w-full text-left'>
                                        <label htmlFor="first_name" className='text-sm font-medium text-gray-500'>
                                            Informe seu nome
                                            {isFieldRequired(validationSchema, 'first_name') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                        </label>
                                        <IMaskInput
                                            id="first_name"
                                            name="first_name"
                                            mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                                            value={values.first_name}
                                            onAccept={(value) => setFieldValue('first_name', value)} // Atualiza o valor no Formik
                                            unmask={true} // true|false|'typed'
                                            autoFocus
                                            placeholder="Digite seu nome aqui"
                                            className="relative block w-full rounded border-0 p-2 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal placeholder:text-sm font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                            disabled={loadingLogin}
                                        />
                                        <ErrorMessage name="first_name" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>
                                </div>

                                <div className='flex gap-2'>
                                    <div className='w-full text-left'>
                                        <label htmlFor="mobile_phone" className='text-sm font-medium text-gray-500'>
                                            Telefone
                                            {isFieldRequired(validationSchema, 'mobile_phone') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                        </label>
                                        <div className='flex items-center justify-between'>
                                            <PhoneInput
                                                inputProps={{
                                                    name: 'mobile_phone',
                                                    required: true,
                                                    autoFocus: false,
                                                    type: 'numeric',
                                                    inputMode: 'numeric',
                                                }}
                                                placeholder='+__ (__) _________'
                                                inputClass='font-bold'
                                                country='br'
                                                value={values.mobile_phone}
                                                onChange={value => setFieldValue('mobile_phone', value)}
                                                disabled={loadingLogin}
                                                masks={{ br: '(..) .....-....' }}
                                            />
                                        </div>
                                        <ErrorMessage name="mobile_phone" component="div" className='text-xs text-shopfood-error pt-1' />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='flex gap-2 p-4 pt-0'>
                            <ButtonPrimary disabled={loadingLogin} type={'submit'} className='gap-1 w-full text-sm' onClick={() => {}}>
                                {loadingLogin && <span><ImSpinner8 className="animate-spin" /></span>}
                                <span>{loadingLogin ? 'Entrando...' : 'Avançar'}</span>
                            </ButtonPrimary>
                            <Button onClick={() => onCancel()} className='text-sm w-11 h-11'>
                                <div><HiXMark size={20} /></div>
                            </Button>
                        </div>

                    </Form>
                )}
            </Formik>
        </>
    )
}

export default FormRegisterCustomerCard;
